import { createContext, useContext, useMemo } from 'react';

import { useMetadataQuery } from './query/useMetadataQuery';
import { getGroupTypeFromFeatureList } from './utils/getGroupTypeFromFeatureList';

export function createABT<T extends string, U extends string>(targetFeature: T, groupTypes: U[]) {
  type ABTCurrentGroupType = U | null;

  interface ABTStatusContextType {
    isLoading: boolean;
    isFetched: boolean;
  }

  const ABTStatusContext = createContext<ABTStatusContextType | null>(null);
  const ABTCurrentGroupTypeContext = createContext<ABTCurrentGroupType | undefined>(undefined);

  function useABTStatusContext() {
    const context = useContext(ABTStatusContext);
    if (!context) {
      throw new Error('useABTStatusContext must be used within an ABTProvider');
    }
    return context;
  }

  function useABTIsFeatureEnabled(groupType: U) {
    const context = useContext(ABTCurrentGroupTypeContext);
    if (context === undefined) {
      throw new Error('useABTCurrentGroupTypeContext must be used within an ABTProvider');
    }

    // 개발 환경이고 context가 groupTypes에 없는 경우 콘솔 경고 출력
    if (process.env.NODE_ENV === 'development') {
      if (context && !groupTypes.includes(context)) {
        console.warn(`[ABT] ${targetFeature}의 그룹 타입 ${context}는 존재하지 않습니다.`);
      }
    }

    return Boolean(context === groupType);
  }

  function useABTCurrentGroupType() {
    const context = useContext(ABTCurrentGroupTypeContext);
    if (context === undefined) {
      throw new Error('useABTCurrentGroupType must be used within an ABTProvider');
    }
    return context;
  }

  interface ABTProviderProps {
    zigzagUuid: string | null | undefined;
    children: React.ReactNode;
  }

  const ABTProvider = (props: ABTProviderProps) => {
    const { zigzagUuid, children } = props;

    const { data, isLoading, isFetched } = useMetadataQuery({
      uuid: zigzagUuid,
    });

    const currentGroupType = useMemo(() => {
      return getGroupTypeFromFeatureList(data?.feature_list, targetFeature, groupTypes);
    }, [data]);

    return (
      <ABTStatusContext.Provider value={{ isLoading, isFetched }}>
        <ABTCurrentGroupTypeContext.Provider value={currentGroupType}>{children}</ABTCurrentGroupTypeContext.Provider>
      </ABTStatusContext.Provider>
    );
  };

  return {
    ABTProvider,
    useABTStatusContext,
    useABTIsFeatureEnabled,
    useABTCurrentGroupType,
  };
}
